/**
 * Hooks Command — Claude Code Session Telemetry
 *
 * Hook handlers invoked by AI coding assistants (Claude Code) to send
 * lightweight session telemetry to the MutagenT API using the traces/spans model.
 *
 * All handlers are non-blocking: failures print a warning to stderr and exit 0.
 *
 * Usage (configured in .claude/settings.local.json):
 *   mutagent hooks claude-code session-start          # stdin: { session_id }
 *   mutagent hooks claude-code session-end            # stdin: { session_id }
 *   mutagent hooks claude-code pre-tool-use           # stdin: { session_id, tool_name, tool_input }
 *   mutagent hooks claude-code post-tool-use          # stdin: { session_id, tool_name, tool_result }
 *   mutagent hooks claude-code post-tool-use-failure  # stdin: { session_id, tool_name, error }
 *   mutagent hooks claude-code user-prompt-submit     # stdin: { session_id, prompt }
 *   mutagent hooks claude-code subagent-start         # stdin: { session_id, agent_type, agent_id }
 *   mutagent hooks claude-code subagent-stop          # stdin: { session_id }
 *   mutagent hooks claude-code pre-compact            # stdin: { session_id }
 *   mutagent hooks claude-code post-compact           # stdin: { session_id, summary }
 */

import { Command } from 'commander';
import {
  safeExecute,
  handleSessionStart,
  handleSessionEnd,
  handlePreToolUse,
  handlePostToolUse,
} from './handlers-core.js';
import {
  handlePostToolUseFailure,
  handleUserPromptSubmit,
  handleStop,
  handleSubagentStart,
  handleSubagentStop,
  handlePreCompact,
  handlePostCompact,
} from './handlers-v2.js';
import { installHooks, SettingsParseError } from './install.js';

export { installHooks, mergeHooksIntoSettings, hasCommand, migrateV1Hooks, SettingsParseError } from './install.js';
export type { ClaudeHookEntry, ClaudeHookMatcher, ClaudeSettingsHooks, ClaudeSettings } from './install.js';

// Re-export internals for testing
export {
  readStdin as _readStdin,
  safeExecute as _safeExecute,
  getString as _getString,
  serializePayload as _serializePayload,
  handleSessionStart as _handleSessionStart,
  handleSessionEnd as _handleSessionEnd,
  handlePreToolUse as _handlePreToolUse,
  handlePostToolUse as _handlePostToolUse,
} from './handlers-core.js';

export {
  handlePostToolUseFailure as _handlePostToolUseFailure,
  handleUserPromptSubmit as _handleUserPromptSubmit,
  handleStop as _handleStop,
  handleSubagentStart as _handleSubagentStart,
  handleSubagentStop as _handleSubagentStop,
  handlePreCompact as _handlePreCompact,
  handlePostCompact as _handlePostCompact,
} from './handlers-v2.js';

export {
  readState as _readState,
  writeState as _writeState,
  deleteState as _deleteState,
  stateFilePath as _stateFilePath,
  pushParent as _pushParent,
  popParent as _popParent,
  peekParent as _peekParent,
  peekParentKind as _peekParentKind,
} from './state.js';

export { sendBatchTrace as _sendBatchTrace } from './api.js';

export {
  installHooks as _installHooks,
  mergeHooksIntoSettings as _mergeHooksIntoSettings,
  migrateV1Hooks as _migrateV1Hooks,
} from './install.js';

export type { HookState, TracePayload, SpanPayload, OpenSpan } from './state.js';

// ============================================================================
// Command definition
// ============================================================================

export function createHooksCommand(): Command {
  const hooks = new Command('hooks')
    .description('Hook handlers for AI coding assistants')
    .addHelpText('after', `
Claude Code Session Telemetry:
  Sends lightweight session activity to the MutagenT traces API for observability.

  Install by adding to .claude/settings.local.json:

  {
    "hooks": {
      "SessionStart": [{"matcher": "startup", "hooks": [{"type": "command", "command": "mutagent hooks claude-code session-start"}]}],
      "Stop": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code session-end"}]}],
      "PreToolUse": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code pre-tool-use"}]}],
      "PostToolUse": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code post-tool-use"}]}],
      "UserPromptSubmit": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code user-prompt-submit"}]}],
      "SubagentStart": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code subagent-start"}]}],
      "SubagentStop": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code subagent-stop"}]}],
      "PreCompact": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code pre-compact"}]}],
      "PostCompact": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code post-compact"}]}],
      "PostToolUseFailure": [{"hooks": [{"type": "command", "command": "mutagent hooks claude-code post-tool-use-failure"}]}]
    }
  }

  Or run: mutagent hooks install
    `);

  // Install subcommand
  hooks
    .command('install')
    .description('Install MutagenT hooks into .claude/settings.local.json (safe merge — never overwrites existing hooks)')
    .option('--cwd <dir>', 'Target directory (defaults to cwd)', process.cwd())
    .addHelpText('after', `
Reads existing .claude/settings.local.json (if present) and deep-merges
MutagenT telemetry hooks into each event array (all 10 events). Skips any
hook already present (checked by command string) so running this multiple
times is safe.
    `)
    .action((opts: { cwd?: string; json?: boolean }) => {
      const targetDir = opts.cwd ?? process.cwd();
      const isJson = Boolean(opts.json);

      let result: ReturnType<typeof installHooks>;
      try {
        result = installHooks(targetDir);
      } catch (err) {
        if (err instanceof SettingsParseError) {
          if (isJson) {
            process.stdout.write(JSON.stringify({
              success: false,
              error: err.message,
              backupPath: err.backupPath,
              settingsPath: err.settingsPath,
            }) + '\n');
          } else {
            process.stderr.write(`[mutagent hooks install] ERROR: ${err.message}\n`);
          }
          process.exit(1);
        }
        throw err;
      }

      for (const { event, command } of result.migrated) {
        process.stderr.write(
          `[mutagent hooks install] ⚠️  Migrated v1 hook: removed '${event} → ${command}' (v2 wires this as 'SessionEnd')\n`
        );
      }

      if (result.added.length === 0 && result.alreadyPresent.length === 0) {
        process.stdout.write(JSON.stringify({
          success: true,
          settingsPath: result.settingsPath,
          added: [],
          alreadyPresent: [],
          message: 'No hooks to install.',
        }) + '\n');
        return;
      }

      // Surface user warning when hooks were actually added
      if (result.userWarning) {
        if (isJson) {
          // In --json mode, the warning appears in the response body as warnings[]
          // (surfaced below in the JSON output)
        } else {
          process.stderr.write(`⚠ ${result.userWarning}\n`);
        }
      }

      const jsonResponse: Record<string, unknown> = {
        success: true,
        settingsPath: result.settingsPath,
        existed: result.existed,
        added: result.added,
        alreadyPresent: result.alreadyPresent,
        message: result.added.length > 0
          ? `Installed ${String(result.added.length)} hook(s). ${String(result.alreadyPresent.length)} already present.`
          : `All hooks already present (${String(result.alreadyPresent.length)}).`,
      };

      if (result.userWarning && isJson) {
        jsonResponse.warnings = [result.userWarning];
      }

      process.stdout.write(JSON.stringify(jsonResponse) + '\n');
    });

  const claudeCode = hooks
    .command('claude-code')
    .description('Claude Code session telemetry');

  // Original 4 hooks
  claudeCode
    .command('session-start')
    .description('Handle session start event')
    .action(async () => { await safeExecute(handleSessionStart); });

  claudeCode
    .command('session-end')
    .description('Handle session end event')
    .action(async () => { await safeExecute(handleSessionEnd); });

  claudeCode
    .command('pre-tool-use')
    .description('Handle pre-tool-use event')
    .action(async () => { await safeExecute(handlePreToolUse); });

  claudeCode
    .command('post-tool-use')
    .description('Handle post-tool-use event')
    .action(async () => { await safeExecute(handlePostToolUse); });

  // New 6 hooks (v2)
  claudeCode
    .command('user-prompt-submit')
    .description('Handle user prompt submit event (creates turn span)')
    .action(async () => { await safeExecute(handleUserPromptSubmit); });

  claudeCode
    .command('stop')
    .description('Handle stop event (closes current turn span)')
    .action(async () => { await safeExecute(handleStop); });

  claudeCode
    .command('subagent-start')
    .description('Handle subagent start event (creates nested agent span)')
    .action(async () => { await safeExecute(handleSubagentStart); });

  claudeCode
    .command('subagent-stop')
    .description('Handle subagent stop event (closes subagent span)')
    .action(async () => { await safeExecute(handleSubagentStop); });

  claudeCode
    .command('pre-compact')
    .description('Handle pre-compact event (opens compaction span)')
    .action(async () => { await safeExecute(handlePreCompact); });

  claudeCode
    .command('post-compact')
    .description('Handle post-compact event (closes compaction span with summary)')
    .action(async () => { await safeExecute(handlePostCompact); });

  claudeCode
    .command('post-tool-use-failure')
    .description('Handle post-tool-use-failure event (closes failed span with error status)')
    .action(async () => { await safeExecute(handlePostToolUseFailure); });

  return hooks;
}
